import { useDispatch } from "react-redux"
import { routePaths } from "../Routes"
import { State } from "../Reducers"
import { useNavigation, Navigation } from "./useNavigation"
import {
    loadMoreJobList,
    loadMoreJobFeedbackMessageList
} from "../Actions/API/Job/JobActions"
import {
    APIJobModel,
    getJobID,
    APIJobListParams,
    defaultParams
} from "../Models/API/JobModel"
import { getInitialData, setList } from "../Models/Client/RemoteData"
import { useAppState } from "./useReduxState"
import { useMemo, useCallback } from "react"
import { JobsDetailRouteParams } from "../Routes/Paths/Dashboard/JobDetailRoute"

/**
 * React hook which returns prev/next navigation handler and `NavigationCursor`
 * which indicates current position in the collection of jobs.
 * If `jobsToNavigateThrough` is provided as an argument, navigation is through
 * within the given array instead of jobs in Redux store.
 */
export function useJobListNavigation(
    orgUsername: string,
    appID: string,
    params: JobsDetailRouteParams,
    jobID?: string,
    jobsToNavigateThrough?: APIJobModel[],
    isMessagesPath?: boolean,
    isReviewView?: boolean
): Navigation | undefined {
    const dispatch = useDispatch()
    const storeData = useAppState((state: State) =>
        isMessagesPath
            ? state.api.jobs.messageTable[appID]
            : state.api.jobs.table[appID]
    )
    const data =
        jobsToNavigateThrough === undefined
            ? storeData
            : setList(
                  getInitialData<APIJobModel, APIJobListParams, string>(),
                  defaultParams,
                  jobsToNavigateThrough,
                  getJobID
              )
    const onLoadMore = useMemo(
        () =>
            jobsToNavigateThrough === undefined
                ? () =>
                      dispatch(
                          isMessagesPath
                              ? loadMoreJobFeedbackMessageList(appID)
                              : loadMoreJobList(appID)
                      )
                : undefined,
        [jobsToNavigateThrough, dispatch, isMessagesPath, appID]
    )
    const buildUrl = useCallback(
        (id: string) =>
            isMessagesPath
                ? isReviewView
                    ? routePaths.jobsMessageReview(
                          orgUsername,
                          appID,
                          id,
                          params
                      )
                    : routePaths.jobsMessageDetail(
                          orgUsername,
                          appID,
                          id,
                          params
                      )
                : isReviewView
                ? routePaths.jobsReview(orgUsername, appID, id, params)
                : routePaths.jobsDetail(orgUsername, appID, id, params),
        [appID, isMessagesPath, orgUsername, params, isReviewView]
    )

    return useNavigation(data, buildUrl, onLoadMore, jobID)
}
